/**
 * O que faz: card "Para revisar" do topo da aba Agentes: quantas sugestões esperam decisão, prévia das
 *            primeiras e o botão que abre o modal de revisão com a conversa ao lado.
 * Onde usa: SecaoAgentes (aba Agentes da página /ia).
 * Conexões: useRevisaoAgentes (mesma consulta do modal, sem pedir de novo) e ModalRevisaoAgentes.
 * Tabelas: indiretamente lead_return_suggestions e lead_classification_state (RPC listar_revisao_agentes).
 * Data: 26/09/2026.
 */
import { useEffect, useState } from 'react';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { ItemRevisao } from '../contratos';
import { useRevisaoAgentes } from '../useAgentes';
import { ListaRevisaoAgentes } from './ListaRevisaoAgentes';
import { ModalRevisaoAgentes } from './ModalRevisaoAgentes';

interface CardParaRevisarProps {
  clinicaId: string;
  modoAdmin: boolean;
  // Vem da URL (?revisar=1), usada pelo sino de avisos para cair direto no modal.
  abrirAoCarregar?: boolean;
  onAbriuAoCarregar?: () => void;
  // Repassado ao modal: "Ajustar regra da coluna" abre o assistente de organização da aba.
  onAjustarOrganizacao?: () => void;
}

// A prévia mostra poucos itens: a leitura e a decisão acontecem no modal.
const ITENS_NA_PREVIA = 3;

export function CardParaRevisar({ clinicaId, modoAdmin, abrirAoCarregar = false, onAbriuAoCarregar, onAjustarOrganizacao }: CardParaRevisarProps) {
  const revisao = useRevisaoAgentes(clinicaId, modoAdmin);
  const itens = revisao.data ?? [];
  const [modalAberto, setModalAberto] = useState(false);
  const [itemInicial, setItemInicial] = useState<ItemRevisao | null>(null);

  useEffect(() => {
    if (!abrirAoCarregar) return;
    setItemInicial(null);
    setModalAberto(true);
    onAbriuAoCarregar?.();
  }, [abrirAoCarregar, onAbriuAoCarregar]);

  const abrir = (item: ItemRevisao | null) => {
    setItemInicial(item);
    setModalAberto(true);
  };

  const etapas = itens.filter((item) => item.tipo === 'etapa').length;
  const retornos = itens.length - etapas;

  return (
    <Card className="lg-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardCheck className="h-4 w-4" aria-hidden="true" />Para revisar
          </CardTitle>
          {/* A contagem muda depois de cada decisão: a frase nova entra com fade. */}
          <CardDescription key={`${revisao.isLoading}-${itens.length}`} className="animate-in fade-in-0 duration-300 motion-reduce:animate-none">
            {revisao.isLoading ? 'Carregando…'
              : itens.length === 0 ? 'Nada esperando decisão agora.'
                : `${etapas} ${etapas === 1 ? 'etapa sugerida' : 'etapas sugeridas'} · ${retornos} ${retornos === 1 ? 'retorno sugerido' : 'retornos sugeridos'}`}
          </CardDescription>
        </div>
        <Button className="shrink-0 rounded-full" disabled={revisao.isLoading || itens.length === 0} onClick={() => abrir(null)}>
          {revisao.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          Revisar agora{itens.length > 0 ? ` (${itens.length})` : ''}
        </Button>
      </CardHeader>
      {revisao.isError && (
        <CardContent className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground animate-in fade-in-0 duration-300 motion-reduce:animate-none">
          Não foi possível carregar as sugestões.
          <Button size="sm" variant="outline" onClick={() => void revisao.refetch()}>Tentar novamente</Button>
        </CardContent>
      )}
      {itens.length > 0 && (
        <CardContent className="p-0 pb-2">
          <ListaRevisaoAgentes itens={itens.slice(0, ITENS_NA_PREVIA)} onSelecionar={abrir} compacta />
          {itens.length > ITENS_NA_PREVIA && (
            <p className="px-4 pt-2 text-xs text-muted-foreground">e mais {itens.length - ITENS_NA_PREVIA} no modal de revisão.</p>
          )}
        </CardContent>
      )}
      <ModalRevisaoAgentes
        aberto={modalAberto}
        onFechar={() => setModalAberto(false)}
        clinicaId={clinicaId}
        modoAdmin={modoAdmin}
        itemInicial={itemInicial}
        onAjustarOrganizacao={onAjustarOrganizacao}
      />
    </Card>
  );
}
